import React, { useEffect, useMemo, useRef, useState } from 'react';
const CARD_FLIP_SOUND = '/assets/whoispaying/shuffling-cards.mp3';
import Dice from './Dice';

export default function RoundRevealOverlay({ open, mode = 'dice', players = [], winners = [], viewerPlayerId, onComplete }) {
  const cardAudioRef = useRef(null);
  const deckRef = useRef(null);
  const handContainerRef = useRef(null);
  const handRefs = useRef([]);
  // Reveal state
  const [revealedCount, setRevealedCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  // Dice rotation state
  const [diceRotation, setDiceRotation] = useState({ x: 0, y: 0 });
  // Legacy single-card flip state (dice mode unchanged)
  const [cardFlipped, setCardFlipped] = useState(false);
  const [cardValue, setCardValue] = useState(null);
  // New: hand of cards (up to 5) for cards mode
  const [handCards, setHandCards] = useState([]); // [{ value, flipped, dealt }]
  const [dealOffsets, setDealOffsets] = useState([]); // [{ x, y }]
  const [dealingIndex, setDealingIndex] = useState(-1);
  const autoCloseTimerRef = useRef(null);
  const dragStartYRef = useRef(null);
  const draggingRef = useRef(false);

  // Find the current viewer's player entry
  const me = useMemo(() => {
    if (!Array.isArray(players) || !viewerPlayerId) return null;
    return players.find((p) => p.playerId === viewerPlayerId) || null;
  }, [players, viewerPlayerId]);
  const values = Array.isArray(me?.values) ? me.values : [];
  // Reset internal state when overlay opens or player changes
  useEffect(() => {
    if (!open) return;
    setRevealedCount(0);
    setIsAnimating(false);
    setDiceRotation({ x: 0, y: 0 });
    setCardFlipped(false);
    setCardValue(null);
    setHandCards([]);
    setDealOffsets([]);
    setDealingIndex(-1);
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  }, [open, viewerPlayerId]);

  // Utility: rotation mapping for a die face (bring face to front)
  const rotationForFace = (face) => {
    switch (Number(face)) {
      case 1: return { x: 0, y: 0 };
      case 2: return { x: 0, y: -90 };
      case 3: return { x: 0, y: 180 };
      case 4: return { x: 0, y: 90 };
      // Top face (5) requires rotating the cube -90deg around X to bring top to front
      case 5: return { x: -90, y: 0 };
      // Bottom face (6) requires rotating the cube +90deg around X to bring bottom to front
      case 6: return { x: 90, y: 0 };
      default: return { x: 0, y: 0 };
    }
  };

  const revealNext = () => {
    if (!open || isAnimating) return;
    if (revealedCount >= values.length) return;
    const nextValue = values[revealedCount];
    setIsAnimating(true);
  if (mode === 'dice') {
      const base = rotationForFace(nextValue);
      // Add some full rotations for a lively roll
      const spinX = base.x + 360 * (1 + Math.floor(Math.random() * 2));
      const spinY = base.y + 360 * (1 + Math.floor(Math.random() * 2));
      setDiceRotation({ x: spinX, y: spinY });
      // Match transition duration in CSS (700ms) + small buffer
      setTimeout(() => {
        setRevealedCount((c) => c + 1);
        setIsAnimating(false);
      }, 800);
    } else {
      // New cards flow uses per-card clicks; no-op here
      setIsAnimating(false);
    }
  };

  // Card drag handlers (drag up to reveal)
  const onCardPointerDown = (e) => {
    if (allRevealed) return;
    draggingRef.current = true;
    dragStartYRef.current = e.clientY ?? (e.touches?.[0]?.clientY);
  };
  const onCardPointerUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const endY = e.clientY ?? (e.changedTouches?.[0]?.clientY);
    const startY = dragStartYRef.current;
    dragStartYRef.current = null;
    if (typeof startY === 'number' && typeof endY === 'number') {
      const deltaY = endY - startY;
      // Negative delta -> dragged up
      if (deltaY < -20) {
        revealNext();
      }
    }
  };

  if (!open) return null;

  // Track the last revealed value for dice
  const lastRevealedValue = revealedCount > 0 ? values[revealedCount - 1] : 1;
  const nextValue = values[revealedCount] || 1;

  // Derive hand values (up to 5) for cards mode
  const handValues = useMemo(() => {
    if (mode !== 'cards') return [];
    const size = Math.min(5, Array.isArray(values) ? values.length : 0);
    return (values || []).slice(0, size);
  }, [mode, values]);

  // Initialize hand cards and animate dealing from deck to hand with accurate offsets
  useEffect(() => {
    if (!open || mode !== 'cards') return;
    const initial = handValues.map((v) => ({ value: v, flipped: false, dealt: false }));
    setHandCards(initial);

    let cancel = false;
    let timers = [];

    const computeOffsetsAndDeal = () => {
      if (cancel) return;
      const deckEl = deckRef.current;
      const handEl = handContainerRef.current;
      if (!deckEl || !handEl) return;
      const deckRect = deckEl.getBoundingClientRect();
      const deckCenterX = deckRect.left + deckRect.width / 2;
      const deckCenterY = deckRect.top + deckRect.height / 2;
      const N = initial.length;
      const offsets = Array.from({ length: N }).map((_, i) => {
        const targetEl = handRefs.current[i];
        const r = targetEl?.getBoundingClientRect();
        if (!r) return { x: 0, y: 0 };
        const cardCenterX = r.left + r.width / 2;
        const cardCenterY = r.top + r.height / 2;
        return {
          x: Math.round(deckCenterX - cardCenterX),
          y: Math.round(deckCenterY - cardCenterY),
        };
      });
      if (!cancel) setDealOffsets(offsets);
      // Stagger deal one-by-one after offsets are ready (slower cadence)
      timers = initial.map((_, i) => setTimeout(() => {
        setDealingIndex(i);
        setHandCards((prev) => prev.map((c, idx) => idx === i ? { ...c, dealt: true } : c));
        // Clear dealing marker a bit after animation starts
        const clearT = setTimeout(() => setDealingIndex((cur) => (cur === i ? -1 : cur)), 420);
        timers.push(clearT);
      }, 500 + i * 320));
    };

    // Wait a frame for layout to be ready
    const raf = requestAnimationFrame(computeOffsetsAndDeal);
    return () => {
      cancel = true;
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [open, mode, handValues]);

  // Derive how many cards in the hand are currently flipped (source of truth for cards mode)
  const cardsRevealedCount = useMemo(
    () => handCards.reduce((count, card) => (card.flipped ? count + 1 : count), 0),
    [handCards]
  );

  // Compute "all revealed" for both modes and start auto-close once the viewer has revealed everything
  const diceAllRevealed = revealedCount >= values.length && values.length > 0;
  const cardsAllRevealed = cardsRevealedCount >= handValues.length && handValues.length > 0;
  const allRevealed = mode === 'cards' ? cardsAllRevealed : diceAllRevealed;

  useEffect(() => {
    if (!open || !allRevealed) return;
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    autoCloseTimerRef.current = setTimeout(() => {
      onComplete && onComplete();
    }, 30000);
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [open, allRevealed, onComplete]);

  // Click to flip a specific hand card
  const onFlipHandCard = (index) => {
    if (mode !== 'cards') return;
    setHandCards((prev) => {
      const next = [...prev];
      const card = next[index];
      if (!card || card.flipped === true || card.dealt !== true) return prev;
      next[index] = { ...card, flipped: true };
      return next;
    });
    // Sound on first flip attempt; progress comes from cardsRevealedCount
    if (cardAudioRef.current) {
      try {
        cardAudioRef.current.currentTime = 0;
        cardAudioRef.current.play();
      } catch {}
    }
  };

  return (
    <div className="reveal-overlay" role="dialog" aria-modal="true">
      <div className="reveal-stage">
        <div className="reveal-title">Reveal your {mode === 'cards' ? 'cards' : 'numbers'}</div>
        <div className="reveal-interactive">
          {mode !== 'cards' ? (
            <Dice
              currentFace={lastRevealedValue}
              nextFace={nextValue}
              onReveal={() => setRevealedCount((c) => c + 1)}
              disabled={allRevealed || isAnimating}
            />
          ) : (
            <div className="card-table" aria-label="Card dealing area">
              {/* Hand on the left/center */}
              <div className="hand-area">
                <div className="hand" role="list" ref={handContainerRef}>
                  {handCards.map((c, i) => (
                    <div
                      key={i}
                      ref={(el) => (handRefs.current[i] = el)}
                      className={`hand-card ${c.dealt ? 'is-dealt' : ''}`}
                      style={{ 
                        transitionDelay: `${i * 120 + 150}ms`, 
                        ['--deal-rot']: `${(i - 2) * 2}deg`,
                        ['--from-x']: `${dealOffsets[i]?.x ?? 220}px`,
                        ['--from-y']: `${dealOffsets[i]?.y ?? -10}px`,
                      }}
                      role="listitem"
                    >
                      <div
                        className={`card hand-card-inner ${i % 2 === 0 ? 'front-a' : 'front-b'}${c.flipped ? ' is-flipped' : ''}`}
                        role="button"
                        tabIndex={0}
                        aria-label={c.flipped ? `Card ${i + 1} revealed` : `Flip card ${i + 1}`}
                        onClick={() => onFlipHandCard(i)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onFlipHandCard(i); } }}
                      >
                        <div className="card-face card-front"/>
                        <div className="card-face card-reveal">{c.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Deck on the right (decorative) */}
              <div className="deck-area" aria-hidden="true">
                <div className="card-stack" aria-label="Deck of cards" ref={deckRef}>
                  {Array.from({ length: 10 }).map((_, i) => {
                    const yOffset = i * 1.5;
                    const rotation = (i % 2 === 0 ? 0.5 : -0.5) * (i * 0.15);
                    const xOffset = (i % 3 - 1) * 0.3;
                    const DECK_SIZE = 10;
                    const activeIdx = dealingIndex >= 0 ? (DECK_SIZE - 1 - dealingIndex) : -1;
                    return (
                      <div
                        key={i}
                        className={`card card-back ${i % 2 === 0 ? 'back-a' : 'back-b'} ${i === activeIdx ? 'deal-active' : ''}`}
                        style={{ transform: `translateY(${yOffset}px) translateX(${xOffset}px) rotate(${rotation}deg)`, zIndex: i }}
                      />
                    );
                  })}
                </div>
              </div>
              <audio ref={cardAudioRef} src={CARD_FLIP_SOUND} preload="auto" />
            </div>
          )}
        </div>
        <div className="reveal-progress" aria-live="polite">
          {(() => {
            const total = mode === 'cards' ? handValues.length : values.length;
            const revealedForDisplay = mode === 'cards' ? cardsRevealedCount : revealedCount;
            if (total <= 0) {
              return <div className="reveal-count">No values yet.</div>;
            }
            return (
            <>
              <div className="reveal-values-inline">
                {(mode === 'cards' ? handValues : values).slice(0, revealedForDisplay).map((v, i) => (
                  <span key={i} className="reveal-chip">{v}</span>
                ))}
                {Array.from({ length: Math.max(0, total - revealedForDisplay) }, (_, i) => (
                  <span key={`p-${i}`} className="reveal-chip pending">?</span>
                ))}
              </div>
              <div className="reveal-count">{revealedForDisplay}/{total} revealed</div>
            </>
            );
          })()}
        </div>
        {/* Hide winners and other players here to build suspense. They'll see the full table after closing. */}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          {allRevealed && (
            <div className="reveal-after-text">
              {mode === 'cards'
                ? "All your cards are revealed. Let's see who won!"
                : "All your rolls are in. Let's see who won!"}
            </div>
          )}
          <button
            type="button"
            className="control-button reveal-close-button"
            aria-label="Close reveal"
            onClick={() => onComplete && onComplete()}
          >
            Close
          </button>
          {!allRevealed && (
            <div style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginTop: 4 }}>
              {mode === 'cards' ? 'Click each of your cards to flip and reveal.' : 'Tap the die to reveal the next number.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
